import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import {
  GetTaskCompletionDocument,
  GetTaskCompletionQuery,
  GetTaskCompletionQueryVariables,
  Task,
  TaskStatus,
  useCompleteTaskMutation,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { logError } from '@src/utils/logNonFatalError';
import { useMixpanel } from '@src/utils/mixpanel';
import { refetchActiveQueriesFresh } from '@utils/refetchActiveQueriesFresh';
import { completeTaskFailureNeedsMessage } from '@utils/tasks/completeTaskFailure';

/** How a completeTask call ended, for callers that hold their own state
 * around the toggle (useSearchTaskCompletion). */
export type CompleteTaskOutcome =
  | { ok: true; completedAt: string | null }
  | { ok: false };

type DoCompleteTaskOptions = {
  isSearch?: boolean;
  /** false: skip the per-toggle list refetch — the caller runs one itself
   * once its burst of toggles has settled (useSearchTaskCompletion). */
  refetch?: boolean;
};

export const useToggleCompleteTask = () => {
  const { client } = useApolloClient();
  const { trackEvent } = useMixpanel();
  const toast = useToast();
  const { t: tShared } = useTranslation('shared');

  const [completeTask] = useCompleteTaskMutation();

  // TA-11018: a rejected completion used to vanish without a trace — the
  // optimistic tick showed, Apollo rolled it back when the response came in,
  // and the row silently reverted. The client-wide `mutate: { errorPolicy:
  // 'all' }` resolves GraphQL errors instead of rejecting, so both shapes are
  // checked: `result.errors` for business failures, the catch for network
  // ones. Always logged; toasted only where errorLink stays silent (e.g.
  // TaskUnassigned, a missing completion photo, or no connection).
  const reportFailure = (
    taskId: string,
    errorMessage: string,
    showToast: boolean
  ) => {
    logError({
      message: 'completeTask failed',
      errorMessage,
      operationName: 'completeTask',
      extra: [{ taskId }],
    });
    if (showToast) {
      toast.show(tShared('actionFailedTryAgain'), {
        type: 'danger',
        duration: 4000,
      });
    }
  };

  // After a failed toggle the row shows Apollo's rollback — the pre-tap state
  // — which is only a guess: a lost response may have committed, and the tap
  // itself may have been made on a stale row. Re-read the task's completion
  // straight from the server into `Task:<id>`, which every surface (search
  // rows included, via useLiveTaskCompletedAt) renders from. It is the only
  // read on the failure path — list refetches there would each raise their
  // own server-error alert (and feed the 503 breaker) while the backend is
  // still failing. Best-effort and silent: the user has been told already.
  const reconcileCompletion = (id: string) => {
    client
      ?.query<GetTaskCompletionQuery, GetTaskCompletionQueryVariables>({
        query: GetTaskCompletionDocument,
        variables: { id },
        fetchPolicy: 'network-only',
        context: {
          queryDeduplication: false,
          suppressServerErrorNotice: true,
        },
      })
      .catch(() => undefined);
  };

  const doCompleteTask = async (
    t: Task,
    { isSearch, refetch = true }: DoCompleteTaskOptions = {}
  ): Promise<CompleteTaskOutcome> => {
    const { id, completedAt, projectId } = t;

    try {
      const result = await completeTask({
        variables: {
          id,
        },
        optimisticResponse: {
          completeTask: {
            __typename: 'Task',
            id: id,
            completedAt: completedAt ? null : new Date().toISOString(),
            status: completedAt ? TaskStatus.Ongoing : TaskStatus.Completed,
          },
        },
      });
      if (result.errors?.length) {
        reconcileCompletion(id);
        reportFailure(
          id,
          result.errors.map((e) => e.message).join('; '),
          completeTaskFailureNeedsMessage({ errors: result.errors })
        );
        return { ok: false };
      }
      // Not the mutation's own `refetchQueries`: those are deduplicated, so a
      // fast second toggle can inherit the first toggle's stale refetch and
      // revert both rows a few seconds later — see refetchActiveQueriesFresh.
      if (client && refetch) {
        refetchActiveQueriesFresh(client, [
          'listTasks',
          ...(isSearch ? ['search'] : []),
        ]);
      }
      // Tracked only once the server accepted it: with errorPolicy 'all' and
      // no onError, Apollo still calls onCompleted for a response carrying
      // errors, which counted every rejected completion as a completion.
      if (completedAt) {
        trackEvent(MIXPANEL_EVENTS.TASK_REOPENED, {
          project_id: projectId,
          task_id: id,
        });
      } else {
        trackEvent(MIXPANEL_EVENTS.TASK_COMPLETED, {
          project_id: projectId,
          task_id: id,
          platform: Platform.OS,
        });
      }
      const confirmed = result.data?.completeTask;
      if (confirmed) {
        return { ok: true, completedAt: confirmed.completedAt ?? null };
      }
      return {
        ok: true,
        completedAt: completedAt ? null : new Date().toISOString(),
      };
    } catch (err) {
      reconcileCompletion(id);
      reportFailure(
        id,
        err instanceof Error ? err.message : String(err),
        completeTaskFailureNeedsMessage({ rejection: err })
      );
      return { ok: false };
    }
  };

  const toggleCompleteTask = (t: Task, isSearch?: boolean) => {
    const cache = client?.cache;
    if (!cache) return;
    doCompleteTask(t, { isSearch });
  };

  const toggleCompleteTaskAsync = async (t: Task, isSearch?: boolean) => {
    const cache = client?.cache;
    if (!cache) return;
    await doCompleteTask(t, { isSearch });
  };

  /** Like toggleCompleteTaskAsync, but reports the outcome and can leave the
   * list refetch to the caller. */
  const completeTaskWithOutcome = async (
    t: Task,
    options?: DoCompleteTaskOptions
  ): Promise<CompleteTaskOutcome> => {
    if (!client?.cache) return { ok: false };
    return doCompleteTask(t, options);
  };

  return {
    toggleCompleteTask,
    toggleCompleteTaskAsync,
    completeTaskWithOutcome,
  };
};
